import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { paginasVisibles } from '../components/Paginador';
import Invitaciones from './Invitaciones';
import { money } from '../events/util';

const INK = '#9A9A9A';

function toCSV(rows) {
  if (!rows.length) return '';
  const keys = Object.keys(rows[0]);
  const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [keys.join(','), ...rows.map(r => keys.map(k => esc(r[k])).join(','))].join('\n');
}
function download(name, content) {
  const blob = new Blob([content], { type: 'text/csv' });
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

export default function EmailMkt() {
  const api = useApi();
  const [tab, setTab] = useState('base');
  const [editor, setEditor] = useState(null); // { type, event_id, audience_filters }

  return (
    <div className="lb-fade">
      <div className="lb-display" style={{ fontSize: 24, marginBottom: 4 }}>La manada, a tu alcance</div>
      <div className="lb-subtabs" style={{ margin: '16px 0 24px' }}>
        <button className={tab === 'base' ? 'active' : ''} onClick={() => { setTab('base'); setEditor(null); }}>Base de datos</button>
        <button className={tab === 'campanas' ? 'active' : ''} onClick={() => { setTab('campanas'); setEditor(null); }}>Campañas</button>
        <button className={tab === 'invitaciones' ? 'active' : ''} onClick={() => { setTab('invitaciones'); setEditor(null); }}>Invitaciones</button>
        <button className={tab === 'historial' ? 'active' : ''} onClick={() => { setTab('historial'); setEditor(null); }}>Historial</button>
      </div>

      {editor ? (
        <Editor api={api} init={editor} onDone={() => { setEditor(null); setTab('historial'); }} onCancel={() => setEditor(null)} />
      ) : (
        <>
          {tab === 'base' && <BaseDatos api={api} onCampaign={(filters) => setEditor({ type: 'base', audience_filters: filters })} />}
          {tab === 'campanas' && <Campanas api={api} onContinue={setEditor} />}
          {tab === 'invitaciones' && <Invitaciones />}
          {tab === 'historial' && <Historial api={api} />}
        </>
      )}
    </div>
  );
}

/* ---------- Filtros reutilizables ---------- */
function useFilterOptions(api) {
  const [opts, setOpts] = useState({ cities: [], lots: [], events: [] });
  useEffect(() => { api.get('/api/marketing/filters').then(setOpts).catch(() => {}); }, []); // eslint-disable-line
  return opts;
}

// Acota la edad al salir del campo: vacío se deja vacío (sin filtro), y cualquier valor
// queda entre 18 (nunca se le manda email a menores) y 120.
function acotarEdad(v) {
  const n = parseInt(String(v).replace(/\D/g, ''), 10);
  if (!Number.isFinite(n)) return '';
  return String(Math.min(120, Math.max(18, n)));
}

function Filters({ opts, filters, setFilters }) {
  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const toggleCity = (c) => setFilters(f => {
    const cities = f.cities.includes(c) ? f.cities.filter(x => x !== c) : [...f.cities, c];
    return { ...f, cities };
  });
  return (
    <div className="lb-card" style={{ padding: '18px 20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: 16 }}>
      {/* El mínimo se aplica al SALIR del campo, no en cada tecla. Antes se corregía a 18
          mientras escribías: al tipear "25", la "2" se convertía en 18 y el resultado
          final era 185. Ahora se escribe libre y se acomoda cuando terminás. */}
      <Field label="Edad (mín – máx) · solo +18">
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="lb-input" inputMode="numeric" placeholder="18" value={filters.ageMin}
            onChange={e => set('ageMin', e.target.value.replace(/\D/g, '').slice(0, 3))}
            onBlur={e => set('ageMin', acotarEdad(e.target.value))} />
          <input className="lb-input" inputMode="numeric" placeholder="99" value={filters.ageMax}
            onChange={e => set('ageMax', e.target.value.replace(/\D/g, '').slice(0, 3))}
            onBlur={e => set('ageMax', acotarEdad(e.target.value))} />
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 4 }}>Nunca se envía a menores de 18 ni a asistentes de eventos de menores.</div>
      </Field>
      <Field label="Origen del contacto">
        <select className="lb-select" value={filters.source} onChange={e => set('source', e.target.value)}>
          <option value="">Todos</option>
          <option value="compradores">Compradores</option>
          <option value="ruleta">Ruleta · nunca compraron</option>
        </select>
        <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 4 }}>Los de la ruleta no tienen edad ni localidad cargada: si usás esos filtros, quedan afuera.</div>
      </Field>
      <Field label="Asistió al evento">
        <select className="lb-select" value={filters.eventId} onChange={e => set('eventId', e.target.value)}>
          <option value="">Cualquiera</option>
          {opts.events.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
        </select>
      </Field>
      <Field label="Mínimo de eventos">
        <input className="lb-input" inputMode="numeric" placeholder="0" value={filters.minEvents} onChange={e => set('minEvents', e.target.value.replace(/\D/g, ''))} />
      </Field>
      <Field label="Lote habitual">
        <select className="lb-select" value={filters.lot} onChange={e => set('lot', e.target.value)}>
          <option value="">Cualquiera</option>
          {opts.lots.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
      </Field>
      <div style={{ gridColumn: '1 / -1' }}>
        <SelectorLocalidades opts={opts} filters={filters} toggleCity={toggleCity} />
      </div>
    </div>
  );
}

// Selector de localidades con buscador.
//
// Antes se listaban TODAS como botones: con una base real eran decenas de chips, con la
// misma ciudad repetida por acentos y mayúsculas (CARCARAÑA / Carcarañá / carcaraña) y
// con basura del campo libre (teléfonos, direcciones). El backend ahora las agrupa y las
// devuelve con su cantidad; acá se muestran las más frecuentes y el resto se busca.
//
// Con un evento de alcance nacional esto es lo único que escala: la lista puede tener
// cientos de localidades y el muro de chips sería inusable.
function SelectorLocalidades({ opts, filters, toggleCity }) {
  const [q, setQ] = useState('');
  const [verTodas, setVerTodas] = useState(false);
  // Compat: si el backend viejo devolvía strings, se normaliza a la forma nueva.
  const ciudades = (opts.cities || []).map(c => (typeof c === 'string' ? { ciudad: c, cantidad: 0 } : c));
  const norm = s => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const t = norm(q.trim());
  const filtradas = t ? ciudades.filter(c => norm(c.ciudad).includes(t)) : ciudades;
  const TOPE = 12;
  const visibles = (t || verTodas) ? filtradas : filtradas.slice(0, TOPE);
  const elegidas = filters.cities;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
        <div className="lb-label" style={{ margin: 0 }}>Localidades</div>
        <span style={{ fontSize: 11.5, color: INK }}>{ciudades.length} en la base</span>
        {elegidas.length > 0 && (
          <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => elegidas.forEach(toggleCity)}>Quitar todas</button>
        )}
      </div>

      {/* Las elegidas siempre visibles arriba: si están mezcladas entre cincuenta chips
          no se sabe qué filtro está puesto. */}
      {elegidas.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {elegidas.map(c => (
            <button key={c} onClick={() => toggleCity(c)} className="lb-btn lb-btn--primary lb-btn--sm">{c} ✕</button>
          ))}
        </div>
      )}

      <input className="lb-input" style={{ maxWidth: 280, marginBottom: 10 }} value={q}
        onChange={e => setQ(e.target.value)} placeholder="Buscar localidad…" />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {visibles.filter(c => !elegidas.includes(c.ciudad)).map(c => (
          <button key={c.ciudad} onClick={() => toggleCity(c.ciudad)} className="lb-btn lb-btn--ghost lb-btn--sm">
            {c.ciudad}{c.cantidad ? <span style={{ color: INK }}> · {c.cantidad}</span> : null}
          </button>
        ))}
        {ciudades.length === 0 && <span style={{ color: INK, fontSize: 12 }}>Sin localidades aún.</span>}
        {t && filtradas.length === 0 && <span style={{ color: INK, fontSize: 12 }}>Ninguna localidad coincide.</span>}
      </div>

      {!t && !verTodas && filtradas.length > TOPE && (
        <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ marginTop: 8 }} onClick={() => setVerTodas(true)}>
          Ver las {filtradas.length - TOPE} restantes
        </button>
      )}
      <div style={{ fontSize: 11, color: INK, marginTop: 8, lineHeight: 1.5 }}>
        El número es la cantidad de <b>personas contactables</b> de esa localidad: el mismo que vas a ver al filtrar. Las escritas de varias formas (con y sin acento, en mayúsculas) se cuentan como una sola.
      </div>
    </>
  );
}

function buildQuery(f) {
  const p = new URLSearchParams();
  if (f.ageMin) p.set('ageMin', f.ageMin);
  if (f.ageMax) p.set('ageMax', f.ageMax);
  if (f.cities.length) p.set('cities', f.cities.join(','));
  if (f.eventId) p.set('eventId', f.eventId);
  if (f.minEvents) p.set('minEvents', f.minEvents);
  if (f.lot) p.set('lot', f.lot);
  if (f.source) p.set('source', f.source);
  if (f.q && f.q.trim()) p.set('q', f.q.trim());
  return p.toString();
}

const EMPTY = { ageMin: '', ageMax: '', cities: [], eventId: '', minEvents: '', lot: '', source: '', q: '' };

/* ---------- TAB 1: Base de datos ---------- */
function BaseDatos({ api, onCampaign }) {
  const opts = useFilterOptions(api);
  const [filters, setFilters] = useState(EMPTY);
  const [contacts, setContacts] = useState([]);
  const [count, setCount] = useState(0);

  // Paginado de a 100. Con 1.000 personas, scrollear hasta el final para ver la última
  // no es una lista, es un castigo. La página se resetea al cambiar los filtros: si no,
  // quedás parado en la página 7 de un resultado que ahora tiene 2.
  const POR_PAGINA = 100;
  const [pag, setPag] = useState(1);
  const totalPaginas = Math.max(1, Math.ceil(contacts.length / POR_PAGINA));
  const paginaActual = Math.min(pag, totalPaginas);
  const desde = (paginaActual - 1) * POR_PAGINA;
  const pagina = contacts.slice(desde, desde + POR_PAGINA);

  const load = useCallback(() => {
    api.get(`/api/marketing/contacts?${buildQuery(filters)}`).then(d => { setContacts(d.contacts); setCount(d.count); }).catch(() => {});
  }, [filters]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPag(1); }, [contacts.length, JSON.stringify(filters)]); // eslint-disable-line

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Filters opts={opts} filters={filters} setFilters={setFilters} />
      {/* Buscador para encontrar a UNA persona (y su teléfono), separado de los filtros
          de segmentación: acá no se arma una audiencia, se busca a alguien. */}
      <input className="lb-input" style={{ maxWidth: 320 }} value={filters.q}
        onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
        placeholder="Buscar persona por nombre, email o teléfono" />
      {filters.q.trim() && (
        <div style={{ fontSize: 11.5, color: INK, marginTop: -10 }}>
          Estás buscando una persona. La búsqueda no cambia la audiencia de la campaña: esa sale con los filtros de arriba.
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: '#fff' }}>
          {count} personas coinciden
          {totalPaginas > 1 && (
            <span style={{ color: INK, fontSize: 12.5, fontWeight: 400, fontFamily: 'var(--font-body)' }}>
              {' '}· mostrando {desde + 1}–{Math.min(desde + POR_PAGINA, contacts.length)}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* El CSV exporta TODO lo que coincide, no la página que se está viendo: si
              bajara solo 100 de 1.000, el archivo mentiría sin avisar. */}
          <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => download('contactos.csv', toCSV(contacts))}>Exportar CSV</button>
          {/* La búsqueda NO viaja a la campaña: es para encontrar a alguien, no para
              definir la audiencia. Si no, buscar "Juan" y apretar acá mandaría la
              campaña a una sola persona creyendo que sale al segmento filtrado. */}
          <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={() => onCampaign({ ...filters, q: '' })} disabled={!count}>Crear campaña con esta audiencia →</button>
        </div>
      </div>
      <div className="lb-card" style={{ padding: '6px 22px' }}>
        <div className="lb-row lb-th" style={{ gridTemplateColumns: '1.3fr 1.6fr 1fr 0.5fr 0.9fr 1.1fr 0.6fr 0.8fr' }}>
          <span>Nombre</span><span>Email</span><span>Teléfono</span><span>Edad</span><span>Localidad</span><span>Último evento</span><span>Eventos</span><span>Origen</span>
        </div>
        {pagina.map((c, i) => (
          <div key={i} className="lb-row" style={{ gridTemplateColumns: '1.3fr 1.6fr 1fr 0.5fr 0.9fr 1.1fr 0.6fr 0.8fr' }}>
            <span style={{ color: '#fff', fontWeight: 600 }}>{c.name} {c.lastname}</span>
            <span style={{ color: INK, fontSize: 13 }}>{c.email}</span>
            {/* Clickeable: en el celular abre el teléfono para llamar o mandar WhatsApp. */}
            <span style={{ fontSize: 13 }}>
              {c.phone
                ? <a href={`tel:${String(c.phone).replace(/[^\d+]/g, '')}`} style={{ color: '#fff', textDecoration: 'none' }}>{c.phone}</a>
                : <span style={{ color: INK }}>—</span>}
            </span>
            <span style={{ color: INK }}>{c.age || '—'}</span>
            <span style={{ color: INK }}>{c.city || '—'}</span>
            <span style={{ color: INK, fontSize: 13 }}>{c.lastEvent || '—'}</span>
            <span style={{ color: '#fff', fontWeight: 600 }}>{c.totalEvents}</span>
            <span style={{ fontSize: 11, color: c.source === 'ruleta' ? 'var(--red-400)' : INK }}>{c.source === 'ruleta' ? 'Ruleta' : 'Compró'}</span>
          </div>
        ))}
        {contacts.length === 0 && (
          <div style={{ padding: '24px 0', textAlign: 'center', color: INK, fontSize: 13, lineHeight: 1.6 }}>
            No hay contactos con esos filtros.<br />
            <span style={{ fontSize: 12 }}>Solo aparecen quienes <b>aceptaron recibir novedades</b> al comprar o al girar la ruleta. Los compradores anteriores no tienen ese consentimiento registrado, por eso no figuran (y los menores de edad nunca figuran).</span>
          </div>
        )}
      </div>

      {totalPaginas > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button className="lb-btn lb-btn--ghost lb-btn--sm" disabled={paginaActual === 1} onClick={() => setPag(p => Math.max(1, p - 1))}>← Anterior</button>
          {paginasVisibles(paginaActual, totalPaginas).map((n, i) => (
            n === '…'
              ? <span key={'g' + i} style={{ color: INK, padding: '0 4px' }}>…</span>
              : <button key={n} onClick={() => setPag(n)}
                  className={'lb-btn lb-btn--sm ' + (n === paginaActual ? 'lb-btn--white' : 'lb-btn--ghost')}
                  style={{ minWidth: 38 }}>{n}</button>
          ))}
          <button className="lb-btn lb-btn--ghost lb-btn--sm" disabled={paginaActual === totalPaginas} onClick={() => setPag(p => Math.min(totalPaginas, p + 1))}>Siguiente →</button>
        </div>
      )}
    </div>
  );
}

/* ---------- TAB 2: Campañas ---------- */
function Campanas({ api, onContinue }) {
  const opts = useFilterOptions(api);
  const [mode, setMode] = useState(null); // 'evento' | 'base'
  const [eventId, setEventId] = useState('');
  const [filters, setFilters] = useState(EMPTY);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (mode === 'base') api.get(`/api/marketing/contacts?${buildQuery(filters)}`).then(d => setCount(d.count)).catch(() => {});
  }, [filters, mode]); // eslint-disable-line

  const CHIPS = ['Recordatorio pre-evento', 'Cambio de fecha o lugar', 'Cancelación', 'Gracias por venir + fotos'];

  if (!mode) return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
      <div className="lb-card" style={{ padding: '26px 24px' }}>
        <div className="lb-h" style={{ fontSize: 17 }}>Comunicá a los de una noche</div>
        <p style={{ color: INK, fontSize: 13 }}>La audiencia son automáticamente los compradores de ese evento.</p>
        <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={() => setMode('evento')}>Envío por evento →</button>
      </div>
      <div className="lb-card" style={{ padding: '26px 24px' }}>
        <div className="lb-h" style={{ fontSize: 17 }}>Envío masivo o segmentado</div>
        <p style={{ color: INK, fontSize: 13 }}>Elegí a quién le llega con los mismos filtros de la base.</p>
        <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={() => setMode('base')}>Campaña a la base →</button>
      </div>
    </div>
  );

  if (mode === 'evento') return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 560 }}>
      <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ alignSelf: 'flex-start' }} onClick={() => setMode(null)}>← Volver</button>
      <Field label="Evento">
        <select className="lb-select" value={eventId} onChange={e => setEventId(e.target.value)}>
          <option value="">Elegí el evento</option>
          {opts.events.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
        </select>
      </Field>
      <div>
        <div className="lb-label" style={{ marginBottom: 8 }}>Casos de uso</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {CHIPS.map(c => <span key={c} className="lb-status lb-status--gen">{c}</span>)}
        </div>
      </div>
      <button className="lb-btn lb-btn--primary" disabled={!eventId} onClick={() => onContinue({ type: 'evento', event_id: eventId })}>Continuar</button>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ alignSelf: 'flex-start' }} onClick={() => setMode(null)}>← Volver</button>
      <Filters opts={opts} filters={filters} setFilters={setFilters} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: '#fff' }}>{count} personas coinciden</div>
        <button className="lb-btn lb-btn--primary" disabled={!count} onClick={() => onContinue({ type: 'base', audience_filters: filters })}>Continuar</button>
      </div>
    </div>
  );
}

/* ---------- EDITOR ---------- */
function Editor({ api, init, onDone, onCancel }) {
  const [f, setF] = useState({ subject: '', body: '', image_url: '', button_text: '', button_url: '' });
  const [org, setOrg] = useState({});
  const [sending, setSending] = useState(false);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));

  useEffect(() => { api.get('/api/org').then(setOrg).catch(() => {}); }, []); // eslint-disable-line

  const onImg = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const r = new FileReader(); r.onload = () => set('image_url', r.result); r.readAsDataURL(file);
  };

  const payload = () => ({ ...f, type: init.type, event_id: init.event_id || null, audience_filters: init.audience_filters || {} });

  async function send() {
    if (!f.subject.trim()) { alert('Poné un asunto'); return; }
    if (!window.confirm('¿Enviar la campaña ahora?')) return;
    setSending(true);
    try { const r = await api.post('/api/marketing/campaigns/send', payload()); alert(`Campaña enviada a ${r.recipients} destinatarios.`); onDone(); }
    catch (e) { alert(e.message); } finally { setSending(false); }
  }
  async function draft() {
    try { await api.post('/api/marketing/campaigns/draft', payload()); alert('Borrador guardado.'); onDone(); }
    catch (e) { alert(e.message); }
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ alignSelf: 'flex-start' }} onClick={onCancel}>← Cancelar</button>
        <Field label="Asunto"><input className="lb-input" value={f.subject} onChange={e => set('subject', e.target.value)} placeholder="Volvé a la manada · Sábado" /></Field>
        <Field label="Texto principal"><textarea className="lb-input" style={{ minHeight: 160 }} value={f.body} onChange={e => set('body', e.target.value)} placeholder="Escribí el cuerpo del email…" /></Field>
        <Field label="Imagen (opcional)">
          <label className="lb-btn lb-btn--ghost lb-btn--sm" style={{ alignSelf: 'flex-start', cursor: 'pointer' }}>
            {f.image_url ? 'Cambiar imagen' : 'Subir imagen'}
            <input type="file" accept="image/*" onChange={onImg} style={{ display: 'none' }} />
          </label>
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Texto del botón"><input className="lb-input" value={f.button_text} onChange={e => set('button_text', e.target.value)} placeholder="COMPRAR ENTRADA" /></Field>
          <Field label="URL del botón"><input className="lb-input" value={f.button_url} onChange={e => set('button_url', e.target.value)} placeholder="https://…" /></Field>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="lb-btn lb-btn--primary" onClick={send} disabled={sending}>{sending ? 'Enviando…' : 'Enviar campaña →'}</button>
          <button className="lb-btn lb-btn--ghost" onClick={draft}>Guardar borrador</button>
        </div>
      </div>

      {/* Preview */}
      <div>
        <div className="lb-eyebrow" style={{ marginBottom: 10 }}>Vista previa</div>
        <div style={{ background: '#000', border: '1px solid var(--border)', borderRadius: 4, padding: 24 }}>
          <div style={{ textAlign: 'center', marginBottom: 18 }}>
            <img src={org.logo_url || '/assets/tukipass-icon.png'} alt="" style={{ height: 40 }} />
          </div>
          {f.image_url && <img src={f.image_url} alt="" style={{ width: '100%', borderRadius: 4, marginBottom: 16 }} />}
          <div style={{ color: '#fff', fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 20, textTransform: 'uppercase', textAlign: 'center' }}>{f.subject || 'Asunto'}</div>
          <div style={{ color: '#C9C9C9', fontSize: 14, lineHeight: 1.6, marginTop: 14, whiteSpace: 'pre-wrap' }}>{f.body || 'Texto del email…'}</div>
          {f.button_text && (
            <div style={{ textAlign: 'center', marginTop: 22 }}>
              <span style={{ display: 'inline-block', background: 'var(--red-600)', color: '#fff', fontWeight: 700, fontSize: 13, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '13px 26px', borderRadius: 4 }}>{f.button_text}</span>
            </div>
          )}
          <div style={{ textAlign: 'center', marginTop: 22, paddingTop: 14, borderTop: '1px solid var(--border)', color: 'var(--ink-600)', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Acceso reservado · Dress code estricto · +18
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- TAB 3: Historial ---------- */
function Historial({ api }) {
  const [list, setList] = useState([]);
  const [detail, setDetail] = useState(null);
  useEffect(() => { api.get('/api/marketing/campaigns').then(setList).catch(() => {}); }, []); // eslint-disable-line

  if (detail) return (
    <div style={{ maxWidth: 640 }}>
      <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setDetail(null)}>← Volver</button>
      <div className="lb-card" style={{ padding: '22px 24px', marginTop: 14 }}>
        <div className="lb-h" style={{ fontSize: 18 }}>{detail.subject}</div>
        <div style={{ color: INK, fontSize: 12, marginTop: 4 }}>{detail.sent_at ? new Date(detail.sent_at).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' }) : 'Borrador'} · {detail.type === 'evento' ? 'EVENTO' : 'BASE'}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, margin: '18px 0' }}>
          <Mini label="Destinatarios" value={detail.recipients_count || 0} />
          <Mini label="Apertura" value={(detail.open_rate || 0) + '%'} />
          <Mini label="Clicks" value={(detail.click_rate || 0) + '%'} />
        </div>
        <div style={{ color: '#C9C9C9', fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap', borderTop: '1px solid var(--border)', paddingTop: 16 }}>{detail.body}</div>
        {detail.button_text && <div style={{ marginTop: 14 }}><span className="lb-status lb-status--used">{detail.button_text} → {detail.button_url}</span></div>}
      </div>
    </div>
  );

  return (
    <div className="lb-card" style={{ padding: '6px 22px' }}>
      <div className="lb-row lb-th" style={{ gridTemplateColumns: '1fr 1.8fr 0.8fr 1fr 0.8fr 0.8fr 0.8fr' }}>
        <span>Fecha</span><span>Asunto</span><span>Tipo</span><span>Destinatarios</span><span>Apertura</span><span>Clicks</span><span></span>
      </div>
      {list.map(c => (
        <div key={c.id} className="lb-row" style={{ gridTemplateColumns: '1fr 1.8fr 0.8fr 1fr 0.8fr 0.8fr 0.8fr' }}>
          <span style={{ color: INK, fontSize: 13 }}>{c.sent_at ? new Date(c.sent_at).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' }) : '—'}</span>
          <span style={{ color: '#fff', fontWeight: 600 }}>{c.subject}{c.status === 'draft' ? ' (borrador)' : ''}</span>
          <span><span className={'lb-status ' + (c.type === 'evento' ? 'lb-status--used' : 'lb-status--gen')}>{c.type === 'evento' ? 'Evento' : 'Base'}</span></span>
          <span style={{ color: '#fff' }}>{c.recipients_count || 0}</span>
          <span style={{ color: INK }}>{c.open_rate || 0}%</span>
          <span style={{ color: INK }}>{c.click_rate || 0}%</span>
          <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setDetail(c)}>Ver</button>
        </div>
      ))}
      {list.length === 0 && <div style={{ padding: '24px 0', textAlign: 'center', color: INK, fontSize: 13 }}>Todavía no enviaste campañas.</div>}
    </div>
  );
}

function Field({ label, children }) {
  return <div className="lb-field"><div className="lb-label">{label}</div>{children}</div>;
}
function Mini({ label, value }) {
  return (
    <div className="lb-card" style={{ padding: '12px 14px' }}>
      <div style={{ fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: INK }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 20, color: '#fff', marginTop: 3 }}>{value}</div>
    </div>
  );
}
